import { dateFilters } from '~/filters/dateFilters';

class InvalidArgumentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidArgumentError';
  }
}

const CAREER_SERVICE_API_VERSION = 1;

/**
 * GraphQLのNodeIDからエンティティIDを抽出します。
 *
 * NodeIDは "API_VERSION_NUMBER:ENTITY_PREFIX#ulid" の形式で、この文字列をbase64エンコードしたものです。
 * この関数は、そのNodeIDをデコードし、エンティティID部分（"ENTITY_PREFIX#ulid"）を抽出します。
 *
 * @example
 * ```
 * const nodeId = "MTIzOkVudGl0eVByZWZpeCM1ZDJlZmEwMi0yNGYxLTExZWItOTdjYi00MDE2Yzg5MWRhYmU=";
 * const entityId = decodeNodeId(nodeId);
 * console.log(entityId); // "ENTITY_PREFIX#5d2efa02-24f1-11eb-97cb-4016c891dabe"
 * ```
 *
 * @param nodeId - 抽出するエンティティIDを含むNodeID
 * @throws {Error} NodeIDが適切な形式でない場合、エラーをスローします
 * @returns エンティティID ("ENTITY_PREFIX#ulid"の形式)
 */
export function decodeNodeId(nodeId: string): string {
  if (!nodeId) {
    return '';
  }

  const decodedStr = atob(nodeId); // base64 decode
  const splitStr = decodedStr.split(':');

  if (!/^\d+$/.test(splitStr[0])) {
    throw new Error('Invalid nodeId format: API version missing or incorrect');
  }

  return splitStr[1];
}

/**
 * GraphQLのエンティティIDからNodeIDを生成します。
 *
 * @param entityId
 * @returns NodeID
 */
export function encodeNodeId(entityId: string): string {
  if (!entityId) {
    return '';
  }

  return btoa(`${CAREER_SERVICE_API_VERSION}:${entityId}`);
}

// AWSDateのフォーマットに変換。YY-MM-DDで3つ共が必須
export const formatToAWSDate = (
  year: string,
  month: string,
  day: string
): string => {
  return dateFilters.toDayjsFormat(`${year}-${month}-${day}`, 'YYYY-MM-DD');
};

/**
 * この関数は、オブジェクトの配列（logsArray）を'id'プロパティでソートし、
 * 対応する配列（careerArray）から 'careerNodeId'を割り当てます。
 *
 * @param logsArray - ソートする必要があるオブジェクトの配列。各オブジェクトには 'id'プロパティが必要です。
 * @param careerArray - ソートされた配列に 'careerNodeId'を提供するオブジェクトの配列。各オブジェクトには 'id'プロパティが必要です。
 *
 * @returns 'id'プロパティに基づいてソートされ、careerArray内の対応するオブジェクトから 'careerNodeId'が割り当てられた新しい配列。
 *
 * @throws {InvalidArgumentError} logsArrayとcareerArrayの長さが同じでない場合、InvalidArgumentErrorが発生します。
 */

export type IdObject = {
  id: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export function sortAndAssignCareerNodeId(
  logsArray: IdObject[],
  careerArray: IdObject[]
): IdObject[] {
  // careerArrayが空の場合、logsArrayのコピーを返します
  if (careerArray.length === 0) {
    return [...logsArray];
  }

  // logsArrayとcareerArrayの長さが同じかどうかを確認します
  if (logsArray.length !== careerArray.length) {
    throw new InvalidArgumentError('logsArrayとcareerArrayの長さは同じである必要があります。');
  }

  // logsArrayの各要素の元のインデックスを含む新しい配列を作成します
  const sortLogs = logsArray.map((log: IdObject, index: number) => ({
    ...log,
    originalIndex: index
  }));

  // 新しい配列を 'id'プロパティに基づいてソートします
  sortLogs.sort((a: IdObject, b: IdObject) => a.id - b.id);

  // careerArray内の対応するオブジェクトから 'careerNodeId'を含む新しい配列を作成します
  const result = sortLogs.map((log: IdObject, index: number) => {
    return { ...log, careerNodeId: careerArray[index].id };
  });

  // 結果の配列を 'originalIndex'プロパティに基づいてソートし、元の順序を復元します
  return result.sort(
    (a: IdObject, b: IdObject) => a.originalIndex - b.originalIndex
  );
}
